import { Trans, useLingui } from "@lingui/react/macro";
import type { CompanyProviderView } from "@rakazo/contracts";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Spinner,
  toast,
} from "@rakazo/ui-web";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useQueryUserId } from "../../lib/query";
import { errorText } from "../../lib/user-error";
import { drokRpc } from "../rpc";
import {
  CompanyProviderForm,
  type FormValues,
  parseHeaders,
  toModels,
} from "./company-models-form";

type Editing = { kind: "new" } | { kind: "edit"; provider: CompanyProviderView } | null;

/** IT-only: the OpenAI-compatible providers whose models employees' locked agents can use. */
export function AdminCompanyModelsPage() {
  const { t } = useLingui();
  const selfId = useQueryUserId();
  const queryClient = useQueryClient();
  const queryKey = ["drok", `user:${selfId ?? ""}`, "admin.company-models"] as const;
  const query = useQuery({
    queryKey,
    queryFn: () => drokRpc.companyModels.list(),
    enabled: selfId !== null,
  });
  const [editing, setEditing] = useState<Editing>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<CompanyProviderView | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["drok", `user:${selfId ?? ""}`] });

  const save = useMutation({
    mutationFn: async (values: FormValues) => {
      const headers = values.headers.trim() ? parseHeaders(values.headers) : undefined;
      const common = {
        name: values.name.trim(),
        baseUrl: values.baseUrl.trim(),
        enabled: values.enabled,
        models: toModels(values.models),
        ...(headers ? { headers } : {}),
      };
      if (editing?.kind === "edit") {
        return drokRpc.companyModels.update({
          id: editing.provider.id,
          ...common,
          ...(values.apiKey.trim() ? { apiKey: values.apiKey.trim() } : {}),
        });
      }
      return drokRpc.companyModels.create({
        ...common,
        slug: values.slug,
        apiKey: values.apiKey.trim(),
      });
    },
    onSuccess: async () => {
      toast.success(t`Company models saved`);
      setEditing(null);
      setSaveError(null);
      await invalidate();
    },
    onError: (e) => setSaveError(errorText(e)),
  });

  const toggle = useMutation({
    mutationFn: (provider: CompanyProviderView) =>
      drokRpc.companyModels.update({
        id: provider.id,
        name: provider.name,
        baseUrl: provider.baseUrl,
        enabled: !provider.enabled,
        models: provider.models,
      }),
    onSuccess: invalidate,
    onError: (e) => toast.error(errorText(e)),
  });

  const remove = useMutation({
    mutationFn: (provider: CompanyProviderView) =>
      drokRpc.companyModels.remove({ id: provider.id }),
    onSuccess: async () => {
      toast.success(t`Provider deleted`);
      setDeleting(null);
      await invalidate();
    },
    onError: (e) => setDeleteError(errorText(e)),
  });

  const providers = query.data?.providers ?? [];
  return (
    <section data-testid="admin-company-models" className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="max-w-prose text-sm text-muted-foreground">
          <Trans>
            Add OpenAI-compatible providers. Their models appear to IT under "Company: name" and can
            be used by locked employee agents. Changes apply within about 30 seconds, with no
            restart.
          </Trans>
        </p>
        <Button
          disabled={editing !== null}
          onClick={() => {
            setSaveError(null);
            setEditing({ kind: "new" });
          }}
        >
          <Trans>Add provider</Trans>
        </Button>
      </div>

      {editing ? (
        <CompanyProviderForm
          key={editing.kind === "edit" ? editing.provider.id : "new"}
          provider={editing.kind === "edit" ? editing.provider : null}
          saving={save.isPending}
          error={saveError}
          onSave={(values) => {
            setSaveError(null);
            save.mutate(values);
          }}
          onCancel={() => setEditing(null)}
        />
      ) : null}

      {query.isPending ? (
        <div className="grid place-items-center p-8">
          <Spinner aria-label={t`Loading…`} />
        </div>
      ) : query.isError ? (
        <div role="alert" className="rounded-xl bg-muted/50 p-4 text-sm">
          <p className="text-destructive">{errorText(query.error)}</p>
          <Button className="mt-2" size="sm" variant="outline" onClick={() => void query.refetch()}>
            <Trans>Try again</Trans>
          </Button>
        </div>
      ) : (
        <>
          {query.data.envProvider ? (
            <div className="rounded-xl border border-dashed border-border p-4 text-sm">
              <p className="font-medium">
                <Trans>Set in the server environment</Trans>
              </p>
              <p className="text-muted-foreground">
                {query.data.envProvider.baseUrlHost} ·{" "}
                <Trans>{query.data.envProvider.modelIds.length} models, read-only</Trans>
              </p>
            </div>
          ) : null}
          {providers.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              <Trans>No providers yet.</Trans>
            </p>
          ) : (
            <ul className="grid gap-3 sm:grid-cols-2">
              {providers.map((provider) => (
                <li
                  key={provider.id}
                  data-testid="company-provider-card"
                  className="space-y-2 rounded-xl border border-border p-4"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h2 className="truncate font-medium">{provider.name}</h2>
                      <p className="truncate text-xs text-muted-foreground">
                        {provider.baseUrlHost}
                      </p>
                    </div>
                    {provider.enabled ? null : (
                      <Badge variant="outline">
                        <Trans>Disabled</Trans>
                      </Badge>
                    )}
                  </div>
                  {provider.unreadable ? (
                    <p role="alert" className="text-sm text-destructive">
                      <Trans>
                        The stored credentials cannot be read. Save the provider again with its API
                        key, or delete it.
                      </Trans>
                    </p>
                  ) : (
                    <p className="text-sm">
                      <Trans>{provider.models.length} models</Trans>
                    </p>
                  )}
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      aria-label={t`Enabled: ${provider.name}`}
                      checked={provider.enabled}
                      disabled={toggle.isPending || provider.unreadable === true}
                      onChange={() => toggle.mutate(provider)}
                    />
                    <Trans>Enabled</Trans>
                  </label>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={editing !== null}
                      onClick={() => {
                        setSaveError(null);
                        setEditing({ kind: "edit", provider });
                      }}
                    >
                      <Trans>Edit</Trans>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setDeleteError(null);
                        setDeleting(provider);
                      }}
                    >
                      <Trans>Delete</Trans>
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      <AlertDialog
        open={deleting !== null}
        onOpenChange={(open) => (open || remove.isPending ? undefined : setDeleting(null))}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <Trans>Delete {deleting?.name ?? ""}?</Trans>
            </AlertDialogTitle>
            <AlertDialogDescription>
              <Trans>
                Its stored key and models are removed. This is refused while any agent or template
                still uses one of its models.
              </Trans>
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deleteError ? (
            <p role="alert" className="text-sm text-destructive">
              {deleteError}
            </p>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={remove.isPending}>
              <Trans>Cancel</Trans>
            </AlertDialogCancel>
            <Button
              variant="destructive"
              disabled={remove.isPending || deleting === null}
              onClick={() => {
                if (!deleting) return;
                setDeleteError(null);
                remove.mutate(deleting);
              }}
            >
              {remove.isPending ? <Spinner aria-label={t`Working…`} /> : null}
              <Trans>Delete</Trans>
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
